import { apiFetch } from './client'

/**
 * Tela 3 (sessao 35) - unidades organizacionais. Leitura (T3) e, desde a s56 (R3), escrita:
 * criar, renomear e trocar a unidade-mae (POST /api/organization-units, PATCH /{id}/basics, PATCH /{id}/parent).
 *
 * Contrato: `GET /api/organization-units` (modules/organization_core/api/router.py),
 * permissao `organization_core.unit.read`. O tenant e o da SESSAO (A132): a SPA
 * nao envia X-Tenant-ID. `count` na resposta e o tamanho da PAGINA, nao o total --
 * por isso a leitura segue pagina a pagina ate vir uma pagina incompleta.
 *
 * TETO: 20 paginas de 200 (4.000 unidades). Acima disso, ERRO EXPLICITO -- nunca uma
 * arvore truncada em silencio (uma unidade faltando parece "nao existe").
 *
 * A135 (sessao 42): a API isola a unidade com dado invalido e a REPORTA em `invalid`
 * (so id + nome do campo). A pagina veio CHEIA quando validas + invalidas = 200 -- parar
 * por `items.length` truncaria a arvore em silencio. Resposta SEM `invalid` (API anterior
 * ao A135: o push no master publica a tela sem a API) = nenhuma invalida.
 */

export type UnitType = 'directorate' | 'department' | 'area' | 'team' | 'other'
export type UnitStatus = 'active' | 'inactive' | 'archived'

export interface Unit {
  id: string
  code: string
  name: string
  shortName: string | null
  unitType: UnitType
  parentId: string | null
  status: UnitStatus
}

interface UnitResponse {
  id: string
  code: string
  name: string
  short_name: string | null
  unit_type: UnitType
  parent_id: string | null
  status: UnitStatus
}

export interface InvalidUnit {
  id: string
  fields: string[]
}

export interface UnitsResult {
  units: Unit[]
  invalid: InvalidUnit[]
}

interface UnitListResponse {
  items: UnitResponse[]
  invalid?: InvalidUnit[]
  limit: number
  offset: number
  count: number
}

export const UNITS_PAGE_SIZE = 200
export const UNITS_MAX_PAGES = 20

export class TooManyUnitsError extends Error {
  constructor() {
    super(`mais de ${UNITS_PAGE_SIZE * UNITS_MAX_PAGES} unidades`)
    this.name = 'TooManyUnitsError'
  }
}

export async function fetchAllUnits(): Promise<UnitsResult> {
  const todas: Unit[] = []
  const invalidas: InvalidUnit[] = []
  for (let pagina = 0; pagina < UNITS_MAX_PAGES; pagina++) {
    const r = await apiFetch<UnitListResponse>(
      `/api/organization-units?limit=${UNITS_PAGE_SIZE}&offset=${pagina * UNITS_PAGE_SIZE}`,
    )
    for (const u of r.items) {
      todas.push({
        id: u.id,
        code: u.code,
        name: u.name,
        shortName: u.short_name,
        unitType: u.unit_type,
        parentId: u.parent_id,
        status: u.status,
      })
    }
    const inv = r.invalid ?? []
    for (const x of inv) invalidas.push({ id: x.id, fields: [...x.fields] })
    if (r.items.length + inv.length < UNITS_PAGE_SIZE) return { units: todas, invalid: invalidas }
  }
  throw new TooManyUnitsError()
}

export interface UnitNode {
  unit: Unit
  children: UnitNode[]
  depth: number
}

const porNome = (a: UnitNode, b: UnitNode) => a.unit.name.localeCompare(b.unit.name, 'pt-BR')

/**
 * Monta a arvore. Mae ausente da lista -> a unidade vira raiz (nao some).
 * Ciclo (A->B->A) -> cada unidade aparece UMA vez; nada trava.
 */
export function buildUnitTree(units: readonly Unit[]): UnitNode[] {
  const ids = new Set(units.map((u) => u.id))
  const filhos = new Map<string, Unit[]>()
  const raizes: Unit[] = []
  for (const u of units) {
    if (u.parentId && ids.has(u.parentId) && u.parentId !== u.id) {
      const l = filhos.get(u.parentId) ?? []
      l.push(u)
      filhos.set(u.parentId, l)
    } else {
      raizes.push(u)
    }
  }
  const visitados = new Set<string>()
  function montar(u: Unit, depth: number): UnitNode {
    visitados.add(u.id)
    const children = (filhos.get(u.id) ?? [])
      .filter((f) => !visitados.has(f.id))
      .map((f) => montar(f, depth + 1))
      .sort(porNome)
    return { unit: u, children, depth }
  }
  const arvore = raizes.map((u) => montar(u, 0)).sort(porNome)
  // Unidades presas num ciclo (nenhuma e raiz) entram como raiz, uma vez cada.
  for (const u of units) {
    if (!visitados.has(u.id)) arvore.push(montar(u, 0))
  }
  return arvore
}

export const UNITS_QUERY_KEY = ['organization-units'] as const

// ---------------------------------------------------------------- s56 / R3: escrita
// Quem autoriza e o servidor (organization_core.unit.write, ADR-0009). A permissao aqui so mostra ou esconde botoes.
export const UNIT_WRITE_PERMISSION = 'organization_core.unit.write'

const CODIGO_OK = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/

/** Mesma regra da API (OrganizationUnitCode): 2 a 64, minusculas, numeros e hifen, sem hifen nas pontas. */
export function codigoValido(codigo: string): boolean {
  return CODIGO_OK.test(codigo)
}

/**
 * Codigo gerado do nome -- a MESMA regra das 22 areas da LUTEPEL (plano s47: "ADMINISTRATIVA / RECURSOS HUMANOS"
 * -> "administrativa-recursos-humanos"): sem acento, minusculo, tudo que nao e letra/numero vira hifen.
 * Resultado fora da regra da API (ex.: 1 letra so) -> '' (o formulario pede o codigo a mao).
 */
export function codigoDoNome(nome: string): string {
  const s = nome
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/g, '')
  return CODIGO_OK.test(s) ? s : ''
}

/** A propria unidade e todas as que estao abaixo dela: nenhuma pode virar mae dela (seria ciclo). */
export function idsDaSubarvore(units: readonly Unit[], id: string): Set<string> {
  const filhos = new Map<string, string[]>()
  for (const u of units) {
    if (u.parentId) {
      const l = filhos.get(u.parentId) ?? []
      l.push(u.id)
      filhos.set(u.parentId, l)
    }
  }
  const fora = new Set<string>([id])
  const fila = [id]
  while (fila.length > 0) {
    const atual = fila.pop() as string
    for (const f of filhos.get(atual) ?? []) {
      if (!fora.has(f)) {
        fora.add(f)
        fila.push(f)
      }
    }
  }
  return fora
}

export interface NewUnit {
  code: string
  name: string
  unitType: UnitType
  parentId: string | null
}

export async function createUnit(n: NewUnit): Promise<string> {
  const r = await apiFetch<UnitResponse>('/api/organization-units', {
    method: 'POST',
    body: { code: n.code, name: n.name, unit_type: n.unitType, parent_id: n.parentId },
  })
  return r.id
}

/** PATCH por PRESENCA: so as chaves enviadas mudam; short_name null limpa. */
export interface UnitBasicsPatch {
  name?: string
  short_name?: string | null
  unit_type?: UnitType
}

export async function updateUnitBasics(id: string, patch: UnitBasicsPatch): Promise<void> {
  await apiFetch(`/api/organization-units/${encodeURIComponent(id)}/basics`, { method: 'PATCH', body: patch })
}

/** parent_id null = topo. A chave vai SEMPRE (o DTO distingue "nao enviado" de null). */
export async function changeUnitParent(id: string, parentId: string | null): Promise<void> {
  await apiFetch(`/api/organization-units/${encodeURIComponent(id)}/parent`, { method: 'PATCH', body: { parent_id: parentId } })
}

/** Erros que a tela explica (o resto vira mensagem generica). 409 serve a DOIS casos: decide pelo `code`. */
export type UnitSaveError = 'duplicate' | 'cycle' | 'parent' | 'generic'

export function unitSaveError(e: unknown): UnitSaveError {
  const d = (e as { detail?: unknown } | null)?.detail
  const code = typeof d === 'object' && d !== null && typeof (d as { code?: unknown }).code === 'string' ? (d as { code: string }).code : ''
  if (code === 'organization_unit_code_already_exists') return 'duplicate'
  if (code === 'would_create_hierarchy_cycle' || code === 'cannot_be_own_parent') return 'cycle'
  if (code === 'parent_organization_unit_not_found' || code === 'parent_must_be_same_tenant') return 'parent'
  return 'generic'
}
